import type { default as _jQuery } from "jquery";
import { watchEffect, computed } from 'vue';
import { type Nil, isNil, exhaustiveGuard, isString, isBoolean, isStringLiteralOfType, type ExpandType } from '@mcwd/typescript-type-guards';
import { inputValueChangedListener } from "../../../utils/input-value-changed-listener.js";
import { AnyMarketoInputName } from "../../types/definitions/inputs/marketo-input-names-and-values.js";
import { useMarketoFormInternals } from "../use-marketo-form-instance.js";
import { useEventListener, computedWithControl } from "@vueuse/core";
import { useFormState } from "../use-form-state.js";
import { useFormLogger } from "../use-form-logger.js";
import { getMarketoInputElement } from "./getMarketoInputElement.js";
import type { MarketoInputNameToHTMLElement } from "./MarketoInputNameToHTMLElement.js";
import { type AnyRawInputElementValue } from "./getRawInputElementValue.js";

export type AnyInputElementValue = ExpandType<AnyRawInputElementValue | boolean | undefined>;


export function useMarketoFormInputElement<
  TInputName extends AnyMarketoInputName,
  TInputElement extends MarketoInputNameToHTMLElement<"marketo", TInputName>,
  TValue extends AnyInputElementValue
> ({
  inputName,
  initialValue,
  callerLabel,
  getValue,
}: {
  inputName: TInputName;
  initialValue: TValue | null;
  getValue: (el: TInputElement) => TValue;
  callerLabel?: string | undefined;
})
{
  const { logger } = useFormLogger({ callerLabel: "useMarketoFormInputElement" });
  const { MarketoFormObj, MarketoFormHtmlElement } = useMarketoFormInternals({
    callerLabel,
  });
  const { FormState } = useFormState({ callerLabel });

  initialValue = isString(initialValue) && initialValue.trim() === "" ? null : initialValue;
  const inputElement = computed<TInputElement | Nil>(() => {
    if (isNil(MarketoFormObj.value)){
      return null;
    }
    const $mktoFormJqueryEl = MarketoFormHtmlElement.value;
    if (isNil($mktoFormJqueryEl) || $mktoFormJqueryEl.length === 0) {
      return null;
    }
    const mktoFormHtmlEl = $mktoFormJqueryEl.get(0);
    if (mktoFormHtmlEl == null){
      return null;
    }
    const formState = FormState.value;
    if (isStringLiteralOfType(formState, ["BeforeLoad", "Loading", "RegularFormComplete"])) {
      return null;
    }
    if (isStringLiteralOfType(formState, ["Setup", "RegularForm", "BlindForm"])) {
      let el = getMarketoInputElement(mktoFormHtmlEl, inputName, logger);
      if (el === null) {
        MarketoFormObj.value?.addHiddenFields({ [inputName]: "" });
        el = getMarketoInputElement(mktoFormHtmlEl, inputName, logger);
      }
      return el as TInputElement | Nil;
    }
    else
    {
      exhaustiveGuard(formState);
    }
  });

  const setInputVal = (
    mktoInput: TInputElement | Nil,
    value: string | string[] | number | boolean | Nil
  ) => {
    if (isNil(mktoInput)) {
      return;
    }
    value = isString(value) && value.trim().length === 0 ? null : value;
    const elVal = getValue(mktoInput);
    if (value !== elVal) {
      if (isBoolean(value)) {
        if (
          mktoInput instanceof HTMLInputElement &&
          mktoInput.type === "checkbox"
        ) {
          $(mktoInput).prop("checked", value);
        } else {
          $(mktoInput).val(value.toString());
        }
      } else {
        $(mktoInput).val(value ?? "");
      }
    }
  };

  const inputValue = computedWithControl(() => inputElement.value, {
    get() {
      return isNil(inputElement.value)? null : getValue(inputElement.value);
    },
    set(value: string | string[] | number | boolean | Nil) {
      const mktoInput = inputElement.value;
      if (isNil(mktoInput)) {
        return;
      }
      const elVal = getValue(mktoInput);
      const newVal = (isString(value) && value.trim() === "") ? null : value ?? null;
      if (newVal === elVal) {
        return;
      }
      setInputVal(mktoInput, newVal);
    },
  });

  const onInputChange = () => {
    inputValue.trigger();
  };

  let inputValueInitialized = false;
  let inputValueChangedListenerId = "";
  useEventListener(
    inputElement,
    inputValueChangedListener.eventName,
    onInputChange
  );

  watchEffect(() => {
    const mktoInput = inputElement.value;
    if (isNil(mktoInput)) {
      if (inputValueChangedListenerId !== "") {
        inputValueChangedListener.removeInput(inputValueChangedListenerId);
        inputValueChangedListenerId = "";
      }
      return;
    }

    if (inputValueChangedListenerId === "") {
      inputValueChangedListenerId = inputValueChangedListener.addInput(mktoInput);
    }

    if (inputValueInitialized === false) {
      if (!isNil(initialValue)) {
        let elValue = $(mktoInput).val() ?? null;
        if (isString(elValue) && elValue.trim() === "") {
          elValue = null;
        }
        // TODO: Make sure that we want to keep initial values that marketo provides.
        if (elValue === "" || isNil(elValue)) {
          inputValue.value = initialValue;
        }
      }
      inputValueInitialized = true;
    }
  });
  return { inputElement, inputValue };
}